/**
 * SeriesTracker - Best-of-3 / Best-of-5 Series Match Tracker
 * Automatically tallies consecutive games, tracking skunks, match points, and offering
 * an instant "Rematch" button when a game ends.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';

export const SERIES_MODES = {
  NONE: 'none',
  UNLIMITED: 'unlimited',
  BO3: 'bo3',
  BO5: 'bo5',
  BO7: 'bo7'
};

export const SERIES_TARGET_WINS = {
  none: 1,
  unlimited: Infinity,
  bo3: 2,
  bo5: 3,
  bo7: 4
};

export class SeriesTracker {
  static STORAGE_KEY = 'crazyCribbage.seriesTracker';

  /**
   * Loads the current series tracking state from storage.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static getSeriesState(storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) {
      return SeriesTracker.getDefaultState();
    }
    try {
      const raw = s.getItem(SeriesTracker.STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data && typeof data === 'object') return data;
      }
    } catch (e) {}
    return SeriesTracker.getDefaultState();
  }

  /**
   * Returns default initial state for a new series.
   * @param {string} [mode='bo3']
   * @returns {Object}
   */
  static getDefaultState(mode = 'bo3') {
    return {
      mode: mode,
      gamesPlayed: 0,
      wins: { p1: 0, p2: 0 },
      skunks: { p1: 0, p2: 0 }, // skunks inflicted by seat
      doubleSkunks: { p1: 0, p2: 0 },
      history: [],
      lastDealer: null,
      champion: null
    };
  }

  /**
   * Saves series state to storage.
   * @param {Object} state
   * @param {Object} [storage=null]
   */
  static saveSeriesState(state, storage = null) {
    const s = storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!s) return;
    try {
      s.setItem(SeriesTracker.STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  /**
   * Sets the series format (e.g. 'bo3', 'bo5', 'unlimited') and resets counts if mode changed.
   * @param {string} mode
   */
  static setMode(mode) {
    const cur = SeriesTracker.getSeriesState();
    if (cur.mode !== mode) {
      const fresh = SeriesTracker.getDefaultState(mode);
      SeriesTracker.saveSeriesState(fresh);
      SeriesTracker.updateSeriesUi();
    }
  }

  /**
   * Records the completion of a game in the series.
   * @param {Object} gameResult
   * @param {string} gameResult.winner Winning seat ('p1', 'p2', 'you', 'cpu')
   * @param {string} [gameResult.winnerName]
   * @param {number} [gameResult.p1Score=0]
   * @param {number} [gameResult.p2Score=0]
   * @param {string} [gameResult.skunkKind] 'skunk' | 'double_skunk' | null
   * @param {string} [gameResult.openingDealer]
   * @returns {Object} Updated series state and status
   */
  static recordGame(gameResult = {}) {
    const state = SeriesTracker.getSeriesState();
    state.gamesPlayed = (state.gamesPlayed || 0) + 1;

    let winKey = 'p1';
    if (gameResult.winner === 'p2' || gameResult.winner === 'cpu') {
      winKey = 'p2';
    } else if (gameResult.winner === 'p1' || gameResult.winner === 'you') {
      winKey = 'p1';
    }

    state.wins[winKey] = (state.wins[winKey] || 0) + 1;

    if (gameResult.skunkKind === 'skunk') {
      state.skunks[winKey] = (state.skunks[winKey] || 0) + 1;
    } else if (gameResult.skunkKind === 'double_skunk') {
      state.doubleSkunks[winKey] = (state.doubleSkunks[winKey] || 0) + 1;
    }

    state.lastDealer = gameResult.openingDealer || state.lastDealer;

    state.history.push({
      gameNumber: state.gamesPlayed,
      winner: winKey,
      winnerName: gameResult.winnerName || winKey,
      p1Score: gameResult.p1Score || 0,
      p2Score: gameResult.p2Score || 0,
      skunkKind: gameResult.skunkKind || null,
      timestamp: Date.now()
    });

    const targetWins = SERIES_TARGET_WINS[state.mode] || Infinity;
    if (state.wins[winKey] >= targetWins) {
      state.champion = winKey;
    }

    SeriesTracker.saveSeriesState(state);
    SeriesTracker.updateSeriesUi();

    return {
      state,
      isComplete: Boolean(state.champion),
      champion: state.champion
    };
  }

  /**
   * Resets the current series for a fresh start.
   * @param {string} [mode]
   */
  static resetSeries(mode) {
    const cur = SeriesTracker.getSeriesState();
    const newMode = mode || cur.mode || 'bo3';
    const fresh = SeriesTracker.getDefaultState(newMode);
    SeriesTracker.saveSeriesState(fresh);
    SeriesTracker.updateSeriesUi();
    return fresh;
  }

  /**
   * Returns the next fair opening dealer for a rematch, alternating from previous game.
   * @param {string} [currentDealer]
   * @returns {string} 'p1' or 'p2'
   */
  static getNextOpeningDealer(currentDealer) {
    const state = SeriesTracker.getSeriesState();
    const prev = currentDealer || state.lastDealer || 'p1';
    return (prev === 'p1' || prev === 'you') ? 'p2' : 'p1';
  }

  /**
   * Builds series standing HTML summary for the end-of-game modal.
   * @param {Object} options
   * @returns {string}
   */
  static buildModalSummary(options = {}) {
    const state = SeriesTracker.getSeriesState();
    const pCount = (typeof window !== 'undefined' && window.settings && window.settings.playerCount) || 2;
    if (!state || state.mode === 'none' || pCount > 2) return '';

    const p1Name = options.p1Name || 'You';
    const p2Name = options.p2Name || 'Computer';
    const p1Wins = state.wins.p1 || 0;
    const p2Wins = state.wins.p2 || 0;
    const targetWins = SERIES_TARGET_WINS[state.mode] || Infinity;
    const modeLabel = state.mode === 'bo3' ? 'Best of 3' : (state.mode === 'bo5' ? 'Best of 5' : (state.mode === 'bo7' ? 'Best of 7' : 'Series'));

    const isChampion = Boolean(state.champion);
    const champName = state.champion === 'p1' ? p1Name : p2Name;

    return `
      <div class="series-modal-card" style="margin: 14px 0; padding: 12px 16px; background: rgba(0,0,0,0.45); border: 1px solid rgba(240,198,74,0.35); border-radius: 10px; text-align: center;">
        <div style="font-size: 0.8rem; font-weight: 700; color: #f0c64a; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 6px;">
          ⚔️ ${modeLabel} Series Tracker
        </div>
        <div style="display: flex; justify-content: center; align-items: center; gap: 16px; margin: 8px 0;">
          <div style="text-align: right; flex: 1;">
            <div style="font-size: 0.95rem; font-weight: 700; color: ${p1Wins > p2Wins ? '#f0c64a' : '#f4ead4'};">${p1Name}</div>
            <div style="font-size: 1.6rem; font-weight: 900; color: #f0c64a; line-height: 1.1;">${p1Wins}</div>
          </div>
          <div style="font-size: 1.2rem; font-weight: 800; color: rgba(244,234,212,0.4);">-</div>
          <div style="text-align: left; flex: 1;">
            <div style="font-size: 0.95rem; font-weight: 700; color: ${p2Wins > p1Wins ? '#f0c64a' : '#f4ead4'};">${p2Name}</div>
            <div style="font-size: 1.6rem; font-weight: 900; color: #f0c64a; line-height: 1.1;">${p2Wins}</div>
          </div>
        </div>
        ${isChampion ? `
          <div style="margin-top: 8px; font-size: 0.95rem; font-weight: 800; color: #34d399; background: rgba(52,211,153,0.15); padding: 6px 12px; border-radius: 6px; border: 1px solid rgba(52,211,153,0.3);">
            👑 ${champName} wins the ${modeLabel} Series!
          </div>
        ` : `
          <div style="margin-top: 6px; font-size: 0.82rem; color: #d6d3d1;">
            Game ${state.gamesPlayed} completed · First to ${targetWins} wins takes the match
          </div>
        `}
      </div>
    `;
  }

  /**
   * Updates or injects the series tracker HUD pill near the board / score chips.
   */
  static updateSeriesUi() {
    if (typeof document === 'undefined') return;

    let pill = document.getElementById('seriesHudPill');
    if (!pill) {
      const topChips = document.getElementById('boardTopChips');
      if (!topChips) return;

      pill = document.createElement('div');
      pill.id = 'seriesHudPill';
      pill.className = 'series-hud-pill';
      pill.title = 'Current match series standings';
      topChips.appendChild(pill);
    }

    const state = SeriesTracker.getSeriesState();
    const pCount = (typeof window !== 'undefined' && window.settings && window.settings.playerCount) || 2;
    if (state.mode === 'none' || pCount > 2) {
      pill.style.display = 'none';
      return;
    }
    pill.style.display = 'inline-flex';

    const modeLabel = state.mode === 'bo3' ? 'Bo3' : (state.mode === 'bo5' ? 'Bo5' : (state.mode === 'bo7' ? 'Bo7' : 'Match'));
    pill.innerHTML = `
      <span class="series-pill-icon">⚔️</span>
      <span class="series-pill-mode">${modeLabel}</span>
      <span class="series-pill-score">${state.wins.p1 || 0} - ${state.wins.p2 || 0}</span>
    `;
  }
}
